'use client'

import {useEffect, useRef} from 'react'
import {createPortal} from 'react-dom'
import {usePathname, useSearchParams} from 'next/navigation'
import type {SiteLite} from '@/multisite/types'
import {
  applyAnalyticsDebugSettingFromQuery,
  buildCtaClickEvent,
  buildPageViewEvent,
  pushDataLayerEvent,
  setAnalyticsContext,
} from '@/lib/analytics'

type AnalyticsPageViewProps = {
  site: SiteLite
}

export function AnalyticsPageView({site}: AnalyticsPageViewProps) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const lastPageViewKeyRef = useRef<string | null>(null)
  const searchParamsString = searchParams?.toString() || ''
  const debugParam = new URLSearchParams(searchParamsString).get('analytics_debug')
  const normalizedDebugParam = debugParam?.trim().toLowerCase()
  const debugEnabledFromParam = normalizedDebugParam
    ? ['1', 'true', 'on', 'yes'].includes(normalizedDebugParam)
      ? true
      : ['0', 'false', 'off', 'no'].includes(normalizedDebugParam)
        ? false
        : undefined
    : undefined
  const debugEnabled = debugEnabledFromParam ?? process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true'
  const showDebugIndicator = Boolean(debugParam) || debugEnabled

  const portalRoot = typeof document !== 'undefined' ? document.body : null

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      const ctaElement = target.closest('[data-analytics-cta-name]')
      if (!(ctaElement instanceof HTMLElement)) return

      const ctaName = ctaElement.dataset.analyticsCtaName?.trim()
      if (!ctaName) return

      const location = ctaElement.dataset.analyticsCtaLocation?.trim() || pathname
      const ctaText = ctaElement.dataset.analyticsCtaText?.trim() || ctaElement.textContent?.trim()
      const ctaHref =
        ctaElement.dataset.analyticsCtaHref?.trim() ||
        (ctaElement instanceof HTMLAnchorElement ? ctaElement.href : undefined)
      const ctaVariant = ctaElement.dataset.analyticsCtaVariant?.trim()

      pushDataLayerEvent(
        buildCtaClickEvent({
          ctaName,
          location,
          ctaText,
          ctaHref,
          ctaVariant,
        }),
      )
    }

    document.addEventListener('click', handleDocumentClick)
    return () => document.removeEventListener('click', handleDocumentClick)
  }, [pathname])

  useEffect(() => {
    applyAnalyticsDebugSettingFromQuery(debugParam)
    setAnalyticsContext({
      siteKey: site.key,
      siteLocale: site.locale,
    })

    const query = searchParamsString
    const pagePath = query ? `${pathname}?${query}` : pathname
    const pageUrl = window.location.href
    const pageViewKey = `${site.key}|${site.locale || ''}|${pagePath}|${pageUrl}`

    if (lastPageViewKeyRef.current === pageViewKey) return
    lastPageViewKeyRef.current = pageViewKey

    pushDataLayerEvent(
      buildPageViewEvent({
        siteKey: site.key,
        siteLocale: site.locale,
        pagePath,
        pageTitle: document.title,
        pageUrl,
      }),
    )
  }, [debugParam, pathname, searchParamsString, site.key, site.locale])

  if (!portalRoot || !showDebugIndicator) return null

  const trackingIds = (site.gtmIds || []).filter(Boolean)
  const trackingIdsLabel = trackingIds.length ? trackingIds.join(', ') : 'none'

  return createPortal(
    <div className="fixed right-3 bottom-3 z-[1000] max-w-[85vw] rounded-lg border border-border bg-card px-3 py-2 text-xs font-semibold shadow">
      <div>Analytics Debug: {debugEnabled ? 'ON' : 'OFF'}</div>
      <div className="mt-0.5 break-words font-normal text-muted-foreground">
        Tracking IDs: {trackingIdsLabel}
      </div>
    </div>,
    portalRoot,
  )
}
